import React, { useState, useEffect, useCallback } from "react";
import { Auth } from "aws-amplify";
import { useNavigate, useLocation } from "react-router-dom";
import { useLoading } from "../../../../context/LoadingContext";
import { jwtDecode } from "jwt-decode";
import "../../../../styles/general.css";
import "../../../../styles/amtpl.css";
import SidebarCommon from "../../../../pages/Requirement/components/layout/AMTPLSidebar";
import Header from "../../../../pages/Requirement/components/layout/Header";
import DataTable from "../../../../pages/Requirement/components/common/ReusableTable";
import { TextField, Typography, Select, MenuItem, FormControl } from "@mui/material";
import { fetchData } from "../../../../pages/Requirement/services/RequirementServices";
import SearchFilter from "../../../../pages/Requirement/components/common/SearchFilter";
import AlertModal from "../../../../pages/Requirement/components/common/AlerModel";
import { v4 as uuidv4 } from "uuid";

const InlandShipping = () => {
  const location = useLocation();
  const initialFilter = location.state?.selectedFilter || "Inland Shipping";
  const [activeUser, setActiveUser] = useState("");
  const [selectedFilter, setSelectedFilter] = useState(initialFilter);
  const { setLoading } = useLoading();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [inLandshippingChargesList, setInLandshippingChargesList] = useState([]);
  const [countrymaster, setCountryMaster] = useState([]);
  // Search dropdown states
  const [originStatesDropdown, setOriginStatesDropdown] = useState([]);
  const [originPortsDropdown, setOriginPortsDropdown] = useState([]);
  const [destinationPortsDropdown, setDestinationPortsDropdown] = useState([]);
  // Form dropdown states
  const [formOriginStatesDropdown, setFormOriginStatesDropdown] = useState([]);
  const [formOriginPortsDropdown, setFormOriginPortsDropdown] = useState([]);
  const [formDestinationPortsDropdown, setFormDestinationPortsDropdown] = useState([]);
  const [rowStart, setRowStart] = useState(0);
  const pageSize = 5;
  const [totalItems, setTotalItems] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });

  const handleModalClose = () => {
    const action = modal.nextAction;
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
    if (action) action();
  };

  const [formData, setFormData] = useState({
    originCountry: "",
    state: "",
    originPort: "",
    destination: "",
    destinationPort: "",
    inlandCharges: "",
    shippingCharges: "",
  });

  const [searchValues, setSearchValues] = useState({
    origincountry: "",
    originstate: "",
    originport: "",
    DestinationCountry: "",
    destinationport: "",
  });

  // Utility function to format price to 2 decimal places
  const formatPrice = (value) => {
    if (!value || value === "") return "";
    const numValue = parseFloat(value);
    return isNaN(numValue) ? "" : numValue.toFixed(2);
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Format price fields to 2 decimal places when user leaves the field
    let formattedValue = value;
    if ((name === "inlandCharges" || name === "shippingCharges") && value !== "") {
      // Only format if it's a valid number and user has finished typing
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        formattedValue = value; // Keep the raw value during typing
      }
    }
    
    setFormData((prev) => ({
      ...prev,
      [name]: formattedValue,
      ...(name === "originCountry" && { state: "", originPort: "" }),
      ...(name === "state" && { originPort: "" }),
      ...(name === "destination" && { destinationPort: "" }),
    }));

    // Load states and ports when country is selected (only for form dropdowns)
    if (name === "originCountry" && value) {
      loadFormStates(value);
      loadFormOriginPorts(value);
    } else if (name === "originCountry" && !value) {
      setFormOriginStatesDropdown([]);
      setFormOriginPortsDropdown([]);
    }

    if (name === "destination" && value) {
      loadFormDestinationPorts(value);
    } else if (name === "destination" && !value) {
      setFormDestinationPortsDropdown([]);
    }
  };

  // Handle blur event for price fields to format to 2 decimal places
  const handlePriceBlur = (e) => {
    const { name, value } = e.target;
    if ((name === "inlandCharges" || name === "shippingCharges") && value !== "") {
      const formattedValue = formatPrice(value);
      if (formattedValue !== "") {
        setFormData((prev) => ({
          ...prev,
          [name]: formattedValue,
        }));
      }
    }
  };

  // Load Countries using proper listDropDownOptions service
  const loadCountries = useCallback(async () => {
    setLoading(true);
    try {
      const [allCountries] = await Promise.all([
        fetchData({
          target: "listDropDownOptions",
          userOrder: "Value",
          rowStart: 0,
          pageSize: 1000,
          filterTerms: {
            Type: "Country",
          },
        }),
      ]);
      
      if (allCountries?.items?.length > 0) {
        setCountryMaster(allCountries.items);
      } else {
        setCountryMaster([]);
      }
    } catch (error) {
      console.error("Error loading countries: ", error);
      setCountryMaster([]);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  // Load Form States using proper listDropDownOptions service
  const loadFormStates = useCallback(async (country) => {
    if (!country) {
      setFormOriginStatesDropdown([]);
      return;
    }
    
    setLoading(true);
    try {
      const [allStates] = await Promise.all([
        fetchData({
          target: "listDropDownOptions",
          userOrder: "Value",
          rowStart: 0,
          pageSize: 1000,
          filterTerms: {
            Type: "State",
            Subtype: country,
          },
        }),
      ]);
      
      if (allStates?.items?.length > 0) {
        setFormOriginStatesDropdown(allStates.items);
      } else {
        setFormOriginStatesDropdown([]);
      }
    } catch (error) {
      console.error("Error loading form states: ", error);
      setFormOriginStatesDropdown([]);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  // Load Form Origin Ports using proper listDropDownOptions service
  const loadFormOriginPorts = useCallback(async (country) => {
    if (!country) {
      setFormOriginPortsDropdown([]);
      return;
    }
    
    setLoading(true);
    try {
      const [allPorts] = await Promise.all([
        fetchData({
          target: "listDropDownOptions",
          userOrder: "Value",
          rowStart: 0,
          pageSize: 1000,
          filterTerms: {
            Type: "port",
            Subtype: country,
          },
        }),
      ]);
      
      if (allPorts?.items?.length > 0) {
        setFormOriginPortsDropdown(allPorts.items);
      } else {
        setFormOriginPortsDropdown([]);
      }
    } catch (error) {
      console.error("Error loading form origin ports: ", error);
      setFormOriginPortsDropdown([]);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  // Load Form Destination Ports using proper listDropDownOptions service
  const loadFormDestinationPorts = useCallback(async (country) => {
    if (!country) {
      setFormDestinationPortsDropdown([]);
      return;
    }
    
    setLoading(true);
    try {
      const [allPorts] = await Promise.all([
        fetchData({
          target: "listDropDownOptions",
          userOrder: "Value",
          rowStart: 0,
          pageSize: 1000,
          filterTerms: {
            Type: "port",
            Subtype: country,
          },
        }),
      ]);
      
      if (allPorts?.items?.length > 0) {
        setFormDestinationPortsDropdown(allPorts.items);
      } else {
        setFormDestinationPortsDropdown([]);
      }
    } catch (error) {
      console.error("Error loading form destination ports: ", error);
      setFormDestinationPortsDropdown([]);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  // Load States using proper listDropDownOptions service (for search)
  const loadStates = useCallback(async (country) => {
    if (!country) {
      setOriginStatesDropdown([]);
      return;
    }
    
    setLoading(true);
    try {
      const [allStates] = await Promise.all([
        fetchData({
          target: "listDropDownOptions",
          userOrder: "Value",
          rowStart: 0,
          pageSize: 1000,
          filterTerms: {
            Type: "State",
            Subtype: country,
          },
        }),
      ]);
      
      if (allStates?.items?.length > 0) {
        setOriginStatesDropdown(allStates.items);
      } else {
        setOriginStatesDropdown([]);
      }
    } catch (error) {
      console.error("Error loading states: ", error);
      setOriginStatesDropdown([]);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  // Load Origin Ports using proper listDropDownOptions service
  const loadOriginPorts = useCallback(async (country) => {
    if (!country) {
      setOriginPortsDropdown([]);
      return;
    }
    
    setLoading(true);
    try {
      const [allPorts] = await Promise.all([
        fetchData({
          target: "listDropDownOptions",
          userOrder: "Value",
          rowStart: 0,
          pageSize: 1000,
          filterTerms: {
            Type: "port",
            Subtype: country,
          },
        }),
      ]);
      
      if (allPorts?.items?.length > 0) {
        setOriginPortsDropdown(allPorts.items);
      } else {
        setOriginPortsDropdown([]);
      }
    } catch (error) {
      console.error("Error loading origin ports: ", error);
      setOriginPortsDropdown([]);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  // Load Destination Ports using proper listDropDownOptions service
  const loadDestinationPorts = useCallback(async (country) => {
    if (!country) {
      setDestinationPortsDropdown([]);
      return;
    }
    
    setLoading(true);
    try {
      const [allPorts] = await Promise.all([
        fetchData({
          target: "listDropDownOptions",
          userOrder: "Value",
          rowStart: 0,
          pageSize: 1000,
          filterTerms: {
            Type: "port",
            Subtype: country,
          },
        }),
      ]);
      
      if (allPorts?.items?.length > 0) {
        setDestinationPortsDropdown(allPorts.items);
      } else {
        setDestinationPortsDropdown([]);
      }
    } catch (error) {
      console.error("Error loading destination ports: ", error);
      setDestinationPortsDropdown([]);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  // Handle search requirements
  const searchRequirements = useCallback(
    async (filterValues = searchValues, newRowStart = rowStart) => {
      setLoading(true);
      try {
        const body = {
          target: "listInlandShippingCharges",
          userOrder: "InlandShippingChargesID",
          rowStart: newRowStart,
          pageSize: pageSize,
          filterTerms: {
            origincountry: filterValues.origincountry || undefined,
            originstate: filterValues.originstate || undefined,
            originport: filterValues.originport || undefined,
            destination: filterValues.DestinationCountry || undefined,
            destinationport: filterValues.destinationport || undefined,
          },
        };
        const response = await fetchData(body);
        setInLandshippingChargesList(response.items || response);
        setTotalItems(response.total || 0);
      } catch (error) {
        console.error("Error loading requirements: ", error);
        setInLandshippingChargesList([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [pageSize, rowStart, searchValues, setLoading]
  );

  // Handle page change
  const handlePageChange = (page) => {
    const newRowStart = (page - 1) * pageSize;
    setRowStart(newRowStart);
    searchRequirements(searchValues, newRowStart);
  };

  // Handle filter change
  const handleDataFilterChange = useCallback(
    (event) => {
      const selectedValue = event.target.value;
      setSelectedFilter(selectedValue);
      if (selectedValue === "products" || selectedValue === "offers" || selectedValue === "entities") {
        navigate("/amtpl", { state: { selectedFilter: selectedValue } });
      } else if (selectedValue === "Add Requirement") {
        navigate("/amtpl/addrequirement", { state: { selectedFilter: selectedValue } });
      } else if (selectedValue === "Requirement Approval") {
        navigate("/amtpl/requirement", { state: { selectedFilter: selectedValue } });
      } else if (selectedValue === "Product Offers") {
        navigate("/amtpl/productofferscharges", { state: { selectedFilter: selectedValue } });
      } else if (selectedValue === "Inland Shipping") {
        navigate("/amtpl/inlandshipping", { state: { selectedFilter: selectedValue } });
      } else if (selectedValue === "Country wise Product") {
        navigate("/amtpl/counrywiseproduct", { state: { selectedFilter: selectedValue } });
      } else if (selectedValue === "Product Category") {
        navigate("/amtpl/productcategory", {
          state: { selectedFilter: selectedValue },
        });
      } else if (selectedValue === "Product") {
        navigate("/amtpl/product", {
          state: { selectedFilter: selectedValue },
        });
      }
    },
    [navigate]
  );

  // Form validation and submission
  const [errors, setErrors] = useState({});
  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      if (!formData[key]) {
        newErrors[key] = "This field is required";
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveFormData = async (data) => {
    setLoading(true);
    try {
      const body = {
        target: isEditMode ? "updateInlandShippingCharge" : "createInlandShippingCharge",
        [isEditMode ? "updateInlandShippingChargeInput" : "createInlandShippingChargeInput"]: {
          InlandShippingChargesID: isEditMode ? editId : uuidv4(),
          OriginCountry: data.originCountry,
          OriginState: data.state,
          OriginPort: data.originPort,
          DestinationCountry: data.destination,
          DestinationPort: data.destinationPort,
          InlandChargesPerMT: formatPrice(data.inlandCharges) || "0.00",
          ShippingChargesPerMT: formatPrice(data.shippingCharges) || "0.00",
          Deleted: 0,
        },
      };
      const response = await fetchData(body);
      console.log(response);
      if (response) {
        setModal({
          show: true,
          type: "success",
          title: "Success!",
          message: isEditMode
            ? "Inland Shipping Charges updated successfully."
            : "Inland Shipping Charges created successfully.",
          autoClose: 2000,
          nextAction: () => {
            navigate("/amtpl/inlandshipping");
            handleReset();
            searchRequirements();
            setShowForm(false);
          },
        });
      }
    } catch (error) {
      console.error("Error saving data:", error);
      setModal({
        show: true,
        type: "error",
        title: "Error!",
        message: `Failed to ${isEditMode ? "update" : "create"} Inland Shipping Charges.`,
        autoClose: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      await saveFormData(formData);
      setFormData({
        originCountry: "",
        state: "",
        originPort: "",
        destination: "",
        destinationPort: "",
        inlandCharges: "",
        shippingCharges: "",
      });
      setErrors([]);
      setIsEditMode(false);
      setEditId(null);
    } else {
      console.log("Validation failed");
    }
  };

  const handleReset = () => {
    setFormData({
      originCountry: "",
      state: "",
      originPort: "",
      destination: "",
      destinationPort: "",
      inlandCharges: "",
      shippingCharges: "",
    });
    // Clear form dropdown states
    setFormOriginStatesDropdown([]);
    setFormOriginPortsDropdown([]);
    setFormDestinationPortsDropdown([]);
    setErrors({});
    setIsEditMode(false);
    setEditId(null);
    setShowForm(false);
  };

  // Handle row click to populate form
  const handleRowClick = (row) => {
    setFormData({
      originCountry: row.OriginCountry || "",
      state: row.OriginState || "",
      originPort: row.OriginPort || "",
      destination: row.DestinationCountry || "",
      destinationPort: row.DestinationPort || "",
      inlandCharges: formatPrice(row.InlandChargesPerMT),
      shippingCharges: formatPrice(row.ShippingChargesPerMT),
    });
    
    // Load form dropdown data for editing
    if (row.OriginCountry) {
      loadFormStates(row.OriginCountry);
      loadFormOriginPorts(row.OriginCountry);
    }
    if (row.DestinationCountry) {
      loadFormDestinationPorts(row.DestinationCountry);
    }
    
    setIsEditMode(true);
    setEditId(row.InlandShippingChargesID);
    setShowForm(true);
    setErrors({});
  };

  // Effect to load initial data
  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);
        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_ADMINS")) {
          await Auth.signOut();
          navigate("/");
          return;
        }
        setActiveUser(user.attributes.email);
      } catch (error) {
        await Auth.signOut();
        navigate("/");
      }
    };

    checkUser();
    loadCountries();
  }, [navigate, loadCountries]);

  // Effect to reload data when rowStart or pageSize changes
  useEffect(() => {
    if (activeUser) {
      searchRequirements();
    }
  }, [rowStart, pageSize, activeUser,searchValues, searchRequirements]);

  // Filter fields configuration
  const fields = [
    {
      name: "origincountry",
      label: "Origin Country",
      type: "dropdown",
      options: countrymaster.map((item) => ({
        value: item.Value,
        label: item.Value,
      })),
      value: searchValues.origincountry,
      onChange: (value) => {
        setSearchValues((prev) => ({
          ...prev,
          origincountry: value,
          originstate: "",
          originport: "",
          destinationport: "",
        }));
        if (value) {
          loadStates(value);
          loadOriginPorts(value);
        } else {
          setOriginStatesDropdown([]);
          setOriginPortsDropdown([]);
        }
      },
    },
    {
      name: "originstate",
      label: "Origin State",
      type: "dropdown",
      options: originStatesDropdown.map((state) => ({
        value: state.Value,
        label: state.Value,
      })),
      value: searchValues.originstate,
      onChange: (value) => {
        setSearchValues((prev) => ({
          ...prev,
          originstate: value,
          originport: "",
        }));
      },
      disabled: !searchValues.origincountry,
    },
    {
      name: "originport",
      label: "Origin Port",
      type: "dropdown",
      options: originPortsDropdown.map((port) => ({
        value: port.Value,
        label: port.Value,
      })),
      value: searchValues.originport,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, originport: value }));
      },
      disabled: !searchValues.origincountry,
    },
    {
      name: "DestinationCountry",
      label: "Destination Country",
      type: "dropdown",
      options: countrymaster.map((item) => ({
        value: item.Value,
        label: item.Value,
      })),
      value: searchValues.DestinationCountry,
      onChange: (value) => {
        setSearchValues((prev) => ({
          ...prev,
          DestinationCountry: value,
          destinationport: "",
        }));
        if (value) {
          loadDestinationPorts(value);
        } else {
          setDestinationPortsDropdown([]);
        }
      },
    },
    {
      name: "destinationport",
      label: "Destination Port",
      type: "dropdown",
      options: destinationPortsDropdown.map((port) => ({
        value: port.Value,
        label: port.Value,
      })),
      value: searchValues.destinationport,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, destinationport: value }));
      },
      disabled: !searchValues.DestinationCountry,
    },
  ];

  // Handle search button click
  const handleSearch = () => {
    setRowStart(0);
    searchRequirements(searchValues, 0);
  };

  const columns = [
    { id: "OriginCountry", numeric: false, label: "Origin (Country)" },
    { id: "OriginState", numeric: false, label: "State" },
    { id: "OriginPort", numeric: false, label: "Port" },
    { id: "DestinationCountry", numeric: false, label: "Destination" },
    { id: "DestinationPort", numeric: false, label: "Port" },
    { 
      id: "InlandChargesPerMT", 
      numeric: true, 
      label: "Inland Charges (Rs./MT)",
      renderCell: (row) => formatPrice(row.InlandChargesPerMT)
    },
    { 
      id: "ShippingChargesPerMT", 
      numeric: true, 
      label: "Shipping Charges (Rs./MT)",
      renderCell: (row) => formatPrice(row.ShippingChargesPerMT)
    },
  ];

  const currentPage = Math.floor(rowStart / pageSize) + 1;

  // Common input styles for Select and TextField
  const commonInputStyles = {
    '& .MuiInputBase-root': {
      height: '40px',
      fontSize:'14px'
    },
    '& .MuiOutlinedInput-input': {
      padding: '10px 14px',fontSize:'14px'
    },
  };

  return (
    <div className="amtpl-dashboard">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
      <Header activeUser={activeUser} setLoading={setLoading} />
      <div className="content">
        <SidebarCommon
          selectedFilter={selectedFilter}
          handleDataFilterChange={handleDataFilterChange}
        />
        <main className="main-content">
          <div className="table-header">
            <div className="header-text">
              <div className="head-text">Inland Transport & Shipping</div>
              <div className="head-subtext">
                All Transport & Shipping data will be displayed here.
              </div>
            </div>
            <div className="head-btn" style={{ display: "flex", gap: "10px", alignItems: "center" , marginRight:'12px'}}>
              <div className="submit-btn" onClick={() => {
                handleReset();
                setShowForm(!showForm);
              }}>
                Add
              </div>
            </div>
          </div>
          <div className="search-filter row">
            <SearchFilter fields={fields} onSearch={handleSearch} />
          </div>
          <DataTable
            columns={columns}
            data={inLandshippingChargesList}
            total={totalItems}
            title="Inland Information"
            onRowClick={handleRowClick}
            rowsPerPage={pageSize}
            onPageChange={handlePageChange}
            currentPage={currentPage}
          />
          {showForm && (
          <div className="box-main">
          <div className="table-header">
            <div className="header-text">
              <div className="head-text"></div>
              <div className="head-subtext">
                
              </div>
            </div>
            
            <div className="head-btn">
              {showForm && (
                <>
                  <div onClick={handleSubmit} className="submit-btn">
                    {isEditMode ? "Update" : "Submit"}
                  </div>
                  <div onClick={handleReset} className="submit-btn">
                    Reset
                  </div>
                </>
              )}
            </div>
          </div>
          
            <form
              onSubmit={handleSubmit}
              style={{ padding: "5px", backgroundColor: "#f9f9f9", borderRadius: "8px" }}
            >
              <div className="row inlandshipping" style={{ gap: "0px" }}>
                <div className="col-4">
                  <FormControl sx={{ minWidth: "100%" }} error={!!errors.originCountry}>
                    <Typography variant="body2">Origin (Country)</Typography>
                    <Select
                      name="originCountry"
                      value={formData.originCountry}
                      onChange={handleChange}
                      displayEmpty
                      sx={{ ...commonInputStyles, backgroundColor: "white" }}
                      inputProps={{ "aria-label": "Select Origin Country" }}
                    >
                      <MenuItem value="" disabled>
                        Select Country
                      </MenuItem>
                      {countrymaster.map((country) => (
                        <MenuItem key={country.Value} value={country.Value}>
                          {country.Value}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.originCountry && (
                      <Typography variant="caption" color="error">
                        {errors.originCountry}
                      </Typography>
                    )}
                  </FormControl>
                </div>
                <div className="col-4">
                  <FormControl sx={{ minWidth: "100%" }} error={!!errors.state}>
                    <Typography variant="body2">State</Typography>
                    <Select
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      displayEmpty
                      sx={{ ...commonInputStyles, backgroundColor: "white" }}
                      inputProps={{ "aria-label": "Select State" }}
                      disabled={!formData.originCountry}
                    >
                      <MenuItem value="" disabled>
                        Select State
                      </MenuItem>
                      {formOriginStatesDropdown.map((state) => (
                        <MenuItem key={state.Value} value={state.Value}>
                          {state.Value}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.state && (
                      <Typography variant="caption" color="error">
                        {errors.state}
                      </Typography>
                    )}
                  </FormControl>
                </div>
                <div className="col-4">
                  <FormControl sx={{ minWidth: "100%" }} error={!!errors.originPort}>
                    <Typography variant="body2">Origin Port</Typography>
                    <Select
                      name="originPort"
                      value={formData.originPort}
                      onChange={handleChange}
                      displayEmpty
                      sx={{ ...commonInputStyles, backgroundColor: "white" }}
                      inputProps={{ "aria-label": "Select Origin Port" }}
                      disabled={!formData.originCountry}
                    >
                      <MenuItem value="" disabled>
                        Select Port
                      </MenuItem>
                      {formOriginPortsDropdown.map((port) => (
                        <MenuItem key={port.Value} value={port.Value}>
                          {port.Value}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.originPort && (
                      <Typography variant="caption" color="error">
                        {errors.originPort}
                      </Typography>
                    )}
                  </FormControl>
                </div>
                <div className="col-4">
                  <FormControl sx={{ minWidth: "100%" }} error={!!errors.destination}>
                    <Typography variant="body2">Destination</Typography>
                    <Select
                      name="destination"
                      value={formData.destination}
                      onChange={handleChange}
                      displayEmpty
                      sx={{ ...commonInputStyles, backgroundColor: "white" }}
                      inputProps={{ "aria-label": "Select Destination Country" }}
                    >
                      <MenuItem value="" disabled>
                        Select Destination
                      </MenuItem>
                      {countrymaster.map((country) => (
                        <MenuItem key={country.Value} value={country.Value}>
                          {country.Value}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.destination && (
                      <Typography variant="caption" color="error">
                        {errors.destination}
                      </Typography>
                    )}
                  </FormControl>
                </div>
                <div className="col-4">
                  <FormControl sx={{ minWidth: "100%" }} error={!!errors.destinationPort}>
                    <Typography variant="body2">Destination Port</Typography>
                    <Select
                      name="destinationPort"
                      value={formData.destinationPort}
                      onChange={handleChange}
                      displayEmpty
                      sx={{ ...commonInputStyles, backgroundColor: "white" }}
                      inputProps={{ "aria-label": "Select Destination Port" }}
                      disabled={!formData.destination}
                    >
                      <MenuItem value="" disabled>
                        Select Port
                      </MenuItem>
                      {formDestinationPortsDropdown.map((port) => (
                        <MenuItem key={port.Value} value={port.Value}>
                          {port.Value}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.destinationPort && (
                      <Typography variant="caption" color="error">
                        {errors.destinationPort}
                      </Typography>
                    )}
                  </FormControl>
                </div>
                <div className="col-4">
                  <div className="mb-3">
                    <Typography variant="body2">Inland Charges (Rs./MT)</Typography>
                    <TextField
                      type="text"
                      name="inlandCharges"
                      value={formData.inlandCharges}
                      onChange={handleChange}
                      onBlur={handlePriceBlur}
                      sx={{ ...commonInputStyles, backgroundColor: "white", width: "100%" }}
                      error={!!errors.inlandCharges}
                      helperText={errors.inlandCharges}
                    />
                  </div>
                </div>
                <div className="col-4">
                  <div className="mb-3">
                    <Typography variant="body2">Shipping Charges (Rs./MT)</Typography>
                    <TextField
                      type="text"
                      name="shippingCharges"
                      value={formData.shippingCharges}
                      onChange={handleChange}
                      onBlur={handlePriceBlur}
                      sx={{ ...commonInputStyles, backgroundColor: "white", width: "100%" }}
                      error={!!errors.shippingCharges}
                      helperText={errors.shippingCharges}
                    />
                  </div>
                </div>
              </div>
            </form>
        </div>  )}
        </main>
      </div>
    </div>
  );
};

export default InlandShipping;